var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo, useState } from "react";
import styled from "styled-components";
import { useAppDispatch, useAppSelector, useQuery } from "../../../../hooks";
import { useErrorHandler } from "../../../../hooks/useErrorHandler";
import { selectMatchState, setMatchState } from "../../../../store/match";
import { setCheckResultShown } from "../../../../api";
import { defaultAvatar } from "../../../../types";
// Единый приглушённый цвет для всех вердиктов. Критично: ни «мафия», ни «мирный»
// не должны отличаться по яркости или цветовой заливке — спящие игроки видят
// изменение освещённости планшета сквозь веки, это ломает механику проверок.
const VERDICT_COLOR = "#C8B98A";
const SUBDUED_TEXT = "#6E6E6E";
const CAPTION_TEXT = "#8A8A8A";
const Overlay = styled.div `
  position: fixed;
  inset: 0;
  background: #050505;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  padding: 48px 64px;
  z-index: 1000;
`;
const Caption = styled.div `
  font-size: 28px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${CAPTION_TEXT};
  text-align: center;
`;
const RemainingLine = styled.div `
  margin-top: 12px;
  font-size: 20px;
  color: ${SUBDUED_TEXT};
  text-align: center;
`;
const Center = styled.div `
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
`;
const Avatar = styled.img `
  width: 180px;
  height: 180px;
  border-radius: 50%;
  object-fit: cover;
  background: #141414;
  border: 1px solid #2a2a2a;
  /* Чуть приглушаем изображение, чтобы не повышать общую яркость экрана. */
  filter: brightness(0.75);
`;
const TargetMeta = styled.div `
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
`;
const TargetPosition = styled.div `
  font-size: 22px;
  color: ${SUBDUED_TEXT};
`;
const TargetName = styled.div `
  font-size: 56px;
  font-weight: 600;
  color: #D8D8D8;
  text-align: center;
`;
const Verdict = styled.div `
  font-size: 128px;
  font-weight: 800;
  letter-spacing: 0.04em;
  color: ${VERDICT_COLOR};
  text-align: center;
  line-height: 1;
  text-transform: uppercase;
`;
const Footer = styled.div `
  display: flex;
  justify-content: center;
  width: 100%;
`;
const ConfirmButton = styled.button `
  background: #1A1A1A;
  color: #D8D8D8;
  border: 1px solid #3A3A3A;
  padding: 20px 72px;
  font-size: 24px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  border-radius: 8px;
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;
export function CheckResultReveal({ checker }) {
    const { param: matchId } = useQuery("matchId");
    const { errorHandler } = useErrorHandler(matchId);
    const dispatch = useAppDispatch();
    const matchState = useAppSelector(selectMatchState);
    const [isRequestSent, setRequestSent] = useState(false);
    const result = useMemo(() => {
        const raw = matchState === null || matchState === void 0 ? void 0 : matchState.state;
        if (!raw || !raw.target)
            return null;
        return raw;
    }, [matchState]);
    const targetPlayer = useMemo(() => {
        if (!result)
            return undefined;
        return matchState === null || matchState === void 0 ? void 0 : matchState.players.find((p) => p.id === result.target.id);
    }, [matchState, result]);
    const onConfirm = () => __awaiter(this, void 0, void 0, function* () {
        if (isRequestSent || !matchId)
            return;
        try {
            setRequestSent(true);
            const next = yield setCheckResultShown(matchId);
            dispatch(setMatchState(next));
        }
        catch (err) {
            setRequestSent(false);
            errorHandler(err);
        }
    });
    if (!result) {
        return null;
    }
    const captionText = checker === "SHERIFF" ? "Проверка Шерифа" : "Проверка Кардинала";
    let verdictText = "";
    if (checker === "SHERIFF") {
        verdictText = result.verdict === "MAFIA" ? "МАФИЯ" : "МИРНЫЙ";
    }
    else {
        verdictText = (result.roleName || "—").toUpperCase();
    }
    const avatarSrc = (targetPlayer === null || targetPlayer === void 0 ? void 0 : targetPlayer.player.photoUrl) || defaultAvatar;
    return (_jsxs(Overlay, { children: [_jsxs("div", { children: [_jsx(Caption, { children: captionText }), checker === "CARDINAL" && result.remainingChecks !== null && (_jsxs(RemainingLine, { children: ["\u041E\u0441\u0442\u0430\u043B\u043E\u0441\u044C \u043F\u0440\u043E\u0432\u0435\u0440\u043E\u043A: ", result.remainingChecks] }))] }), _jsxs(Center, { children: [_jsx(Avatar, { src: avatarSrc, alt: "" }), _jsxs(TargetMeta, { children: [_jsxs(TargetPosition, { children: ["\u041C\u0435\u0441\u0442\u043E \u2116", result.target.position] }), _jsx(TargetName, { children: result.target.name })] }), _jsx(Verdict, { children: verdictText })] }), _jsx(Footer, { children: _jsx(ConfirmButton, Object.assign({ type: "button", onClick: onConfirm, disabled: isRequestSent }, { children: "\u041F\u043E\u043A\u0430\u0437\u0430\u043B" })) })] }));
}
